import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useTheme } from '@mui/material/styles';
import { useColorMode } from '../index';
import {
  Menu as MenuIcon,
  Dashboard,
  Assignment,
  People,
  Person,
  Logout,
  School,
  AdminPanelSettings,
  Announcement,
  Chat as ChatIcon,
  Group,
  Schedule,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useQuery } from 'react-query';

// Always use the same baseURL as the API client to avoid mismatches
const API_ROOT = (api.defaults.baseURL as string) || 'https://capstone-worker.tp071695.workers.dev';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const theme = useTheme();
  const { toggle } = useColorMode();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Projects', icon: <Assignment />, path: '/projects' },
    { text: 'Teams', icon: <Group />, path: '/teams' },
    { text: 'Chat', icon: <ChatIcon />, path: '/chat' },
    { text: 'Meetings', icon: <Schedule />, path: '/meetings' },
    { text: 'Performance', icon: <TrendingUp />, path: '/performance' },
    ...(user?.role === 'admin' ? [
      { text: 'Users', icon: <People />, path: '/users' },
      { text: 'Announcements', icon: <Announcement />, path: '/announcements' },
    ] : []),
  ];

  // Announcements popup state
  const [annOpen, setAnnOpen] = useState(false);
  type AnnouncementItem = {
    id: number;
    title: string;
    content: string;
    priority: 'low' | 'medium' | 'high';
    created_by_name: string;
    created_at: string;
  };
  const { data: announcements = [], isLoading: loadingAnnouncements } = useQuery<AnnouncementItem[]>(
    'announcements-popup',
    async () => {
      const res = await api.get('/announcements');
      return res.data.announcements;
    },
    { enabled: annOpen }
  );

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Project Management
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100vw' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'saturate(180%) blur(8px)',
          backgroundColor: (theme) => theme.palette.primary.main,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Final Project Management System
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title="Announcements">
              <span>
                <IconButton color="inherit" onClick={() => setAnnOpen(true)} aria-label="announcements">
                  <Badge color="error" variant="dot" overlap="circular">
                    <Announcement />
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
            <IconButton color="inherit" onClick={toggle} aria-label="toggle theme">
              {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600 }}>
              {user?.full_name}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar 
                key={(user?.avatar_url || 'no-avatar') + (localStorage.getItem('token')?.slice(-6) || '')}
                src={user?.avatar_url ? `${API_ROOT}${user.avatar_url}?v=${localStorage.getItem('token')?.slice(-6) || ''}` : undefined}
                sx={{ width: 36, height: 36, border: '2px solid rgba(255,255,255,0.8)' }}
              >
                {user?.role === 'admin' ? <AdminPanelSettings /> : 
                 user?.role === 'supervisor' ? <School /> : <Person />}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: 'calc(100vh - 64px)',
          boxSizing: 'border-box',
          maxWidth: '100vw',
          overflowX: 'hidden',
        }}
      >
        {children}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
      >
        <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      {/* Announcements Dialog */}
      <Dialog open={annOpen} onClose={() => setAnnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Announcements</DialogTitle>
        <DialogContent dividers>
          {loadingAnnouncements ? (
            <Typography variant="body2">Loading...</Typography>
          ) : announcements.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No announcements yet.</Typography>
          ) : (
            <List>
              {announcements.slice(0, 10).map((a) => (
                <ListItem key={a.id} alignItems="flex-start" divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>{a.title}</Typography>
                        <Chip size="small" label={a.priority} color={a.priority === 'high' ? 'error' : a.priority === 'medium' ? 'warning' : 'info'} />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{a.content}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          By {a.created_by_name} • {new Date(a.created_at).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnnOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout; 